import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, Filter } from 'lucide-react';
import FilterableDataTable from '../../../components/ui/FilterableDataTable';
import SearchBar from '../../../components/ui/SearchBar';
import AnchorDropdownPanel from '../../../components/ui/AnchorDropdownPanel';
import DropdownSearch from '../../../components/ui/DropdownSearch';
import SelectDropdown from '../../../components/ui/SelectDropdown';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import { dropdown, table, cn } from '../../../components/ui/uiTheme';
import { SectionSubtitle } from '../../../components/ui/PageLayout';
import { dashboardPullRequestsColumns } from '../dashboardActivityColumns';
import ServerTablePagination from '../ServerTablePagination';
import {
  isClientOnlyPrSort,
  prSortToServer,
  useDashboardPullRequestsQuery,
  useDashboardReposForFilter,
} from '../hooks/useDashboardActivityQueries';

const DEFAULT_PR_SORT = [{ id: 'pr', desc: true }];

const PR_SORT_OPTIONS = [
  { key: 'pr-desc', columnId: 'pr', desc: true, label: 'Date (newest first)' },
  { key: 'pr-asc', columnId: 'pr', desc: false, label: 'Date (oldest first)' },
  { key: 'pr-files-desc', columnId: 'prFiles', desc: true, label: 'Files changed (high → low)' },
  { key: 'pr-files-asc', columnId: 'prFiles', desc: false, label: 'Files changed (low → high)' },
  { key: 'pr-churn-desc', columnId: 'prChurn', desc: true, label: 'Churn ratio (high → low)' },
  { key: 'pr-churn-asc', columnId: 'prChurn', desc: false, label: 'Churn ratio (low → high)' },
  { key: 'pr-add-desc', columnId: 'prAdditions', desc: true, label: 'Additions (high → low)' },
  { key: 'pr-add-asc', columnId: 'prAdditions', desc: false, label: 'Additions (low → high)' },
  { key: 'pr-del-desc', columnId: 'prDeletions', desc: true, label: 'Deletions (high → low)' },
  { key: 'pr-del-asc', columnId: 'prDeletions', desc: false, label: 'Deletions (low → high)' },
];

const PR_STATE_OPTIONS = [
  { value: 'all', label: 'All states' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed (not merged)' },
  { value: 'merged', label: 'Merged' },
];

const DEFAULT_LIMIT = 50;

export default function DashboardPullRequestsTab({
  orgId,
  active,
}: {
  orgId: string | undefined;
  active: boolean;
}) {
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [serverSort, setServerSort] = useState({ sortBy: 'createdAt', sortDir: 'desc' });
  const [prSearch, setPrSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [prSort, setPrSort] = useState(DEFAULT_PR_SORT);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filterAnchorRef = useRef<HTMLButtonElement>(null);
  const sortAnchorRef = useRef<HTMLButtonElement>(null);

  const [appliedState, setAppliedState] = useState('all');
  const [appliedRepositoryId, setAppliedRepositoryId] = useState('');
  const [draftPrState, setDraftPrState] = useState('all');
  const [draftRepositoryId, setDraftRepositoryId] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(prSearch.trim()), 350);
    return () => window.clearTimeout(t);
  }, [prSearch]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, appliedState, appliedRepositoryId, serverSort, limit]);

  const queryParams = useMemo(
    () => ({
      limit,
      offset,
      sortBy: serverSort.sortBy,
      sortDir: serverSort.sortDir,
      state: appliedState,
      ...(appliedRepositoryId ? { repositoryId: appliedRepositoryId } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [limit, offset, serverSort, appliedState, appliedRepositoryId, debouncedSearch]
  );

  const prQuery = useDashboardPullRequestsQuery(orgId, active, queryParams);
  const reposQuery = useDashboardReposForFilter(orgId, active);

  const pullRequests = prQuery.data?.pullRequests ?? [];
  const stats = prQuery.data?.stats;
  const meta = prQuery.data?.meta;
  const total = meta?.total ?? 0;
  const loading = prQuery.isPending;

  const repoOptions = useMemo(() => {
    const rows = reposQuery.data ?? [];
    return [
      { id: '', label: 'All repositories' },
      ...rows.map((r: any) => ({
        id: r.id,
        label: r.fullName || r.name || r.id,
      })),
    ];
  }, [reposQuery.data]);

  useEffect(() => {
    if (!filterOpen) return;
    setDraftPrState(appliedState);
    setDraftRepositoryId(appliedRepositoryId || '');
  }, [filterOpen, appliedState, appliedRepositoryId]);

  const hasActivePrFilters = appliedState !== 'all' || Boolean(appliedRepositoryId);

  const handleApplyFilters = () => {
    setAppliedState(draftPrState || 'all');
    setAppliedRepositoryId(draftRepositoryId?.trim() || '');
    setFilterOpen(false);
  };

  const handleClearAppliedFilters = () => {
    setAppliedState('all');
    setAppliedRepositoryId('');
    setDraftPrState('all');
    setDraftRepositoryId('');
    setFilterOpen(false);
  };

  const handleSortPick = (opt: (typeof PR_SORT_OPTIONS)[number]) => {
    setPrSort([{ id: opt.columnId, desc: opt.desc }]);
    if (!isClientOnlyPrSort(opt.columnId)) {
      setServerSort(prSortToServer(opt.columnId, opt.desc));
    }
    setSortOpen(false);
  };

  return (
    <div
      role="tabpanel"
      id="dashboard-tabpanel-pull-requests"
      aria-labelledby="dashboard-tab-pull-requests"
    >
      <FeatureGate flag="ui.dashboard.pullRequestsTab">
        <div className={cn(table.wrapper, 'overflow-hidden')}>
          <div className="px-4 py-3 border-b border-dark-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1">
              <SectionSubtitle>Pull requests</SectionSubtitle>
              <p className="text-sm text-slate-400 mt-0.5">
                {stats != null && (
                  <>
                    Open: {stats.open ?? 0} · Closed: {stats.closed ?? 0}
                    {stats.merged != null && <> · Merged: {stats.merged}</>}
                    <span className="text-slate-600" aria-hidden>
                      {' '}
                      ·{' '}
                    </span>
                  </>
                )}
                {total > 0 && <>Total: {total} · </>}
                Organisation-wide pull requests across assigned repositories.
              </p>
            </div>
            {!loading && (
              <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:max-w-xl md:max-w-2xl">
                <div className="min-w-0 flex-1 sm:min-w-[12rem] sm:max-w-md">
                  <SearchBar
                    value={prSearch}
                    onChange={setPrSearch}
                    placeholder="Search title or author…"
                  />
                </div>
                <button
                  ref={sortAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setSortOpen((v) => !v)}
                  aria-label="Sort pull requests"
                  title="Sort pull requests"
                  aria-expanded={sortOpen}
                  aria-haspopup="dialog"
                  aria-controls="dashboard-pr-sort-menu"
                >
                  <ArrowUpDown className="h-5 w-5 text-slate-200" aria-hidden />
                </button>
                <button
                  ref={filterAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-label="Filter pull requests"
                  title="Filter pull requests"
                  aria-expanded={filterOpen}
                  aria-haspopup="dialog"
                  aria-controls="dashboard-pr-filter-menu"
                >
                  <span className="relative inline-flex">
                    <Filter className="h-5 w-5 text-slate-200" aria-hidden />
                    {hasActivePrFilters && (
                      <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-vivid-red ring-2 ring-dark-surface"
                        aria-hidden
                      />
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>

          <FilterableDataTable
            key={`dashboard-pr-${orgId}`}
            data={pullRequests}
            columns={dashboardPullRequestsColumns}
            getRowId={(row) => String(row.id)}
            isLoading={loading}
            emptyMessage="No pull requests found."
            initialSorting={DEFAULT_PR_SORT}
            sorting={prSort}
            onSortingChange={setPrSort}
            showColumnHeaders={false}
            hideTopSearchRow
            enablePagination={false}
          />

          <ServerTablePagination
            total={total}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            onLimitChange={(next) => {
              setLimit(next);
              setOffset(0);
            }}
            idPrefix="dashboard-pr"
          />

          <AnchorDropdownPanel
            open={sortOpen}
            onClose={() => setSortOpen(false)}
            anchorRef={sortAnchorRef}
            id="dashboard-pr-sort-menu"
            ariaLabel="Sort pull requests"
            panelWidth={300}
          >
            <div className="space-y-1" role="list">
              {PR_SORT_OPTIONS.map((opt) => {
                const activeSort =
                  prSort.length === 1 &&
                  prSort[0].id === opt.columnId &&
                  prSort[0].desc === opt.desc;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="listitem"
                    className={cn(
                      'w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                      activeSort ? 'bg-brand-purple/15 text-brand-purple-light' : dropdown.item
                    )}
                    onClick={() => handleSortPick(opt)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </AnchorDropdownPanel>

          <AnchorDropdownPanel
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            anchorRef={filterAnchorRef}
            id="dashboard-pr-filter-menu"
            title="Filter pull requests"
            titleIcon={<Filter className="h-5 w-5 shrink-0 text-vivid-red" aria-hidden />}
            panelWidth={400}
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="dashboard-pr-filter-state"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  State
                </label>
                <SelectDropdown
                  id="dashboard-pr-filter-state"
                  className="w-full"
                  value={draftPrState}
                  onChange={setDraftPrState}
                  options={PR_STATE_OPTIONS}
                  compact
                />
              </div>
              <div>
                <label
                  htmlFor="dashboard-pr-filter-repo"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  Repository
                </label>
                <DropdownSearch
                  id="dashboard-pr-filter-repo"
                  options={repoOptions}
                  value={draftRepositoryId}
                  onChange={(opt) => setDraftRepositoryId(opt?.id ?? '')}
                  getOptionValue={(o) => o.id}
                  getOptionLabel={(o) => o.label}
                  placeholder="Search repositories…"
                  emptyMessage="No matching repository"
                  compact
                />
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={() => setFilterOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={handleClearAppliedFilters}
                  disabled={!hasActivePrFilters && draftPrState === 'all' && !draftRepositoryId}
                >
                  Clear filters
                </button>
                <button type="button" className="btn-primary-mdl-sm" onClick={handleApplyFilters}>
                  Apply
                </button>
              </div>
            </div>
          </AnchorDropdownPanel>
        </div>
      </FeatureGate>
    </div>
  );
}
